import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon, Send as SendIcon } from '@mui/icons-material';

interface RegistrationRow {
  id: string;
  email: string;
  role: string;
  firstName?: string;
  lastName?: string;
  isActive?: boolean;
  emailVerified?: boolean;
  createdAt?: string;
  subscriptionTierHint?: string | null;
  subscriptionStatus?: string;
}

interface DigestRow {
  id: string;
  windowStart?: string;
  windowEnd?: string;
  registrationsCount: number;
  ticketsCount: number;
  emailSent?: boolean;
  createdAt?: string;
}

function token() {
  return localStorage.getItem('accessToken') || localStorage.getItem('token');
}

/** Customer ops: registrations by date + digest history. */
const AdminCustomerOps: React.FC = () => {
  const [rows, setRows] = useState<RegistrationRow[]>([]);
  const [digests, setDigests] = useState<DigestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [total, setTotal] = useState(0);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (role) params.set('role', role);
      if (status) params.set('status', status);
      if (search) params.set('search', search);
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      params.set('limit', '100');

      const t = token();
      const headers = t ? { Authorization: `Bearer ${t}` } : {};

      const [regRes, digRes] = await Promise.all([
        fetch(`/api/admin/registrations?${params.toString()}`, { headers }),
        fetch('/api/admin/ops-digests', { headers }),
      ]);
      const regData = await regRes.json();
      const digData = await digRes.json();
      if (!regData.success) throw new Error(regData.message || 'Failed to load registrations');
      setRows(regData.data || []);
      setTotal(regData.pagination?.total || 0);
      if (digData.success) setDigests(digData.data || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runDigest = async () => {
    try {
      const t = token();
      const res = await fetch('/api/admin/ops-digests/run', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {}),
        },
        body: JSON.stringify({ sinceHours: 8 }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Digest failed');
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Digest failed');
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
        Customer ops — registrations
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Date-wise user registrations with filters. Ops digests email 2–3× daily based on volume
        (set OPS_DIGEST_RECIPIENTS). Subscription column uses the local purchase ledger when a
        yearly plan is active; otherwise it shows registration status + tier hint.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={1.5} sx={{ mb: 2 }} alignItems="center">
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Role</InputLabel>
            <Select label="Role" value={role} onChange={(e) => setRole(String(e.target.value))}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="player">player</MenuItem>
              <MenuItem value="coach">coach</MenuItem>
              <MenuItem value="academy_admin">academy_admin</MenuItem>
              <MenuItem value="sponsor">sponsor</MenuItem>
              <MenuItem value="admin">admin</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={status} onChange={(e) => setStatus(String(e.target.value))}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">active</MenuItem>
              <MenuItem value="inactive">inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={() => void load()} sx={{ textTransform: 'none' }}>
              Apply
            </Button>
            <Button startIcon={<RefreshIcon />} onClick={() => void load()} sx={{ textTransform: 'none' }}>
              Refresh
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Typography variant="body2" sx={{ mb: 1 }}>
        Showing {rows.length} of {total} users
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff', mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 800 }}>Registered</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Subscription</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {[row.firstName, row.lastName].filter(Boolean).join(' ') || '—'}
                  </TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>
                    <Chip size="small" label={row.role} sx={{ fontWeight: 700 }} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={row.isActive ? 'success' : 'default'}
                      label={row.isActive ? 'active' : 'inactive'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {row.subscriptionTierHint || '—'} · {row.subscriptionStatus || 'registered'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Ops digests
        </Typography>
        <Button
          variant="outlined"
          startIcon={<SendIcon />}
          onClick={() => void runDigest()}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Run digest now
        </Button>
      </Stack>
      <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 800 }}>Created</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Window</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Registrations</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Tickets</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Email</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {digests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary">No digests yet — run one or wait for the scheduler.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              digests.map((d) => (
                <TableRow key={d.id}>
                  <TableCell>{d.createdAt ? new Date(d.createdAt).toLocaleString() : '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    {d.windowStart ? new Date(d.windowStart).toLocaleString() : '—'} →{' '}
                    {d.windowEnd ? new Date(d.windowEnd).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell>{d.registrationsCount}</TableCell>
                  <TableCell>{d.ticketsCount}</TableCell>
                  <TableCell>
                    <Chip size="small" color={d.emailSent ? 'success' : 'default'} label={d.emailSent ? 'sent' : 'logged'} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AdminCustomerOps;
